import type { AxiosProgressEvent } from "axios";
import axios from "axios";
import qs from "qs";
import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { splitFileIntoChunks } from "@entrilia/shared/utilities/fileHelper";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { stringToBase64 } from "@entrilia/shared/utilities/stringHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";

// https://learn.microsoft.com/en-us/rest/api/storageservices/operations-on-block-blobs

const uploadBlobToBlobStorage = async (
  sasUrl: string,
  blob: Blob,
  onUploadProgress?: (e: AxiosProgressEvent) => void
): Promise<ApiResponse<null>> => {
  const resp = await axios.put(sasUrl, blob, {
    headers: {
      "Content-Type": blob.type || "application/octet-stream",
      "x-ms-blob-type": "BlockBlob",
    },
    onUploadProgress,
  });

  return resp.status === 201
    ? createApiResponse(null)
    : createErrorApiResponse({ message: "Failed to upload file to blob storage" });
};

const uploadBlobBlockToBlobStorage = async (
  sasUrl: string,
  blob: Blob,
  base64BlockId: string,
  onUploadProgress?: (e: AxiosProgressEvent) => void
): Promise<ApiResponse<null>> => {
  const queryString = qs.stringify({ comp: "block", blockid: base64BlockId });
  const url = `${sasUrl}&${queryString}`;

  const resp = await axios.put(url, blob, {
    headers: {
      "Content-Type": blob.type || "application/octet-stream",
    },
    onUploadProgress,
  });

  return resp.status === 201
    ? createApiResponse(null)
    : createErrorApiResponse({ message: "Failed to upload blob as block to blob storage" });
};

const composeBlobBlocksToBlob = async (sasUrl: string, blockIds: string[]): Promise<ApiResponse<null>> => {
  const xmlBody = `<?xml version="1.0" encoding="utf-8"?>
<BlockList>
  ${blockIds.map((blockId) => `<Latest>${blockId}</Latest>`).join("")}
</BlockList>`;

  const queryString = qs.stringify({ comp: "blocklist" });
  const url = `${sasUrl}&${queryString}`;

  const resp = await axios.put(url, xmlBody, {
    headers: {
      "Content-Type": "application/xml",
    },
  });

  return resp.status === 201
    ? createApiResponse(null)
    : createErrorApiResponse({ message: "Failed to compose blob blocks to blob" });
};

const maxBlockSize = 1_000_000_000; // 1 GB

const uploadFileToBlobStorage = async (
  sasUrl: string,
  file: File,
  onUploadProgress?: (e: AxiosProgressEvent) => void
): Promise<ApiResponse<null>> => {
  if (file.size <= maxBlockSize) {
    return uploadBlobToBlobStorage(sasUrl, file, onUploadProgress);
  }

  const chunks = splitFileIntoChunks(file, maxBlockSize);
  const blockIds: string[] = [];
  for (let i = 0; i < chunks.length; ++i) {
    const blob = defined(chunks[i]);
    const blockId = stringToBase64(generateGuid());
    blockIds.push(blockId);

    const resp = await uploadBlobBlockToBlobStorage(sasUrl, blob, blockId, (e: AxiosProgressEvent) => {
      if (onUploadProgress && e.progress) {
        onUploadProgress({ ...e, progress: e.progress / chunks.length + i / chunks.length });
      }
    });

    if (!resp.success) {
      return resp;
    }
  }

  return await composeBlobBlocksToBlob(sasUrl, blockIds);
};

const blobStorageApi = {
  uploadFileToBlobStorage,
};

export default blobStorageApi;
